import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import {
    type BearbeitungAktion,
    type BearbeitungStand,
    type Markierung,
} from "./pdfBearbeitung";
import { textRechteckeDerSelection } from "./auswahlText";
import { auswahlZuRechtecken, cssZuPdfRect, pdfZuCssRect, type ViewportLike } from "./pdfKoordinaten";
import { alleDokumente, alleFenster, aktivesDokument } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Entfernen-Ebene über dem PDF.js-Viewer (PO 28.09.2026: „Ausschneiden sowie
 * Löschen von Text/Bildern bzw. Selektionen“).
 *
 * Hier wird nur MARKIERT — gelöscht wird beim Speichern auf dem Server (MuPDF),
 * und zwar echt: der Inhalt verschwindet aus der Datei, der Rest bleibt Text.
 *
 * - Rechteck aufziehen: auf einer Stelle ohne Text (mit Alt auch über Text).
 * - Text auswählen → Entf/Rücktaste oder der schwebende Knopf.
 * - Strg+X: Text bzw. Bildausschnitt in die Zwischenablage, dann markieren.
 * - × an der Markierung nimmt sie zurück.
 *
 * Schwärzen (T-363) läuft über dieselbe Ebene (`schwaerzen`): gleiche Gesten,
 * die Markierung trägt `schwarz` und erscheint als schwarzer Balken. Der Server
 * entfernt genauso echt und malt danach den Balken in die Datei.
 *
 * Die Markierungen hängen per Portal in den `.page`-Divs von PDF.js. PDF.js
 * räumt diese Divs beim Zoomen leer (`PDFPageView.reset`) — deshalb wird der
 * Portal-Host nach jedem Render-Ereignis wieder eingehängt.
 */

/** Was die Ebene vom PDF.js-Viewer braucht (echter `PDFViewer` erfüllt das). */
export interface SeitenAnsichtLike {
    div: HTMLElement;
    viewport: ViewportLike & { width: number };
    canvas?: HTMLCanvasElement | null;
}
export interface ViewerLike {
    pagesCount: number;
    getPageView(index: number): SeitenAnsichtLike | undefined | null;
    eventBus: { on(name: string, fn: () => void): void; off(name: string, fn: () => void): void };
}

/** Kleiner als das (CSS-px) gilt als versehentlicher Klick. */
const MIN_ZUG = 4;

const RENDER_EREIGNISSE = ["pagerendered", "textlayerrendered", "scalechanging", "rotationchanging", "pagesinit"];

let zaehler = 0;
const neueId = () => `m${Date.now().toString(36)}${(++zaehler).toString(36)}`;

/** Inhaltsfläche einer Seite im Fenster — ohne den (transparenten) Rahmen des `.page`-Divs. */
export const inhaltsBox = (div: HTMLElement): DOMRect => {
    const r = div.getBoundingClientRect();
    const bx = div.clientLeft || 0;
    const by = div.clientTop || 0;
    return new DOMRect(r.left + bx, r.top + by, r.width - 2 * bx, r.height - 2 * by);
};

/** Tastendrücke in Eingabefeldern (auch dem Freitext-Editor von PDF.js) gehören diesen. */
const istEingabe = (ziel: EventTarget | null) => {
    const el = ziel as HTMLElement | null;
    if (!el || typeof el.closest !== "function") return false;
    return !!el.closest("input, textarea, select, [contenteditable=''], [contenteditable='true'], [contenteditable='plaintext-only']");
};

/**
 * Die aktuelle, nicht leere Textauswahl, die im Viewer beginnt oder endet —
 * sonst `null`. Wer beim Ziehen über den Rand hinaus (z. B. in die Leiste)
 * gerät, soll trotzdem markieren können; markiert werden ohnehin nur die
 * Stücke, die auf einer Seite liegen.
 */
const auswahlImViewer = (container: HTMLElement | null): Selection | null => {
    const sel = window.getSelection?.();
    if (!container || !sel || sel.isCollapsed || sel.rangeCount === 0) return null;
    const drin = (n: Node | null) => !!n && container.contains(n);
    return drin(sel.anchorNode) || drin(sel.focusNode) ? sel : null;
};

type Zug = { seite: number; x0: number; y0: number; x1: number; y1: number };

const stil = {
    markierung: {
        position: "absolute",
        pointerEvents: "auto",
        boxSizing: "border-box",
        border: "1px solid rgba(160, 20, 20, 0.9)",
        // Dunkles Rot 35 % mit Schraffur: sichtbar „kommt weg“, ohne den Inhalt zu verdecken.
        background:
            "repeating-linear-gradient(135deg, rgba(160,20,20,0.35) 0 4px, rgba(160,20,20,0.18) 4px 8px)",
        cursor: "pointer",
    } as React.CSSProperties,
    markierungAusgewaehlt: { outline: "2px solid rgba(160, 20, 20, 1)", outlineOffset: "1px" } as React.CSSProperties,
    // Schwärzen: fast deckend schwarz — so sieht es nach dem Speichern aus.
    schwarz: { border: "1px solid #000", background: "rgba(0, 0, 0, 0.85)" } as React.CSSProperties,
    kreuz: {
        position: "absolute",
        top: -9,
        right: -9,
        width: 18,
        height: 18,
        borderRadius: 9,
        border: "1px solid rgba(160, 20, 20, 0.9)",
        background: "#fff",
        color: "rgb(160, 20, 20)",
        fontSize: 12,
        lineHeight: "16px",
        padding: 0,
        cursor: "pointer",
        pointerEvents: "auto",
    } as React.CSSProperties,
    zug: {
        position: "absolute",
        border: "1px dashed rgb(160, 20, 20)",
        background: "rgba(160, 20, 20, 0.12)",
        pointerEvents: "none",
    } as React.CSSProperties,
    knopf: {
        position: "fixed",
        zIndex: 1000,
        padding: "3px 8px",
        fontSize: 12,
        border: "1px solid rgba(160, 20, 20, 0.9)",
        borderRadius: 4,
        background: "#fff",
        color: "rgb(160, 20, 20)",
        cursor: "pointer",
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
    } as React.CSSProperties,
};

export const EntfernenEbene: React.FC<{
    viewer: ViewerLike | null;
    /** Scrollender Container des Viewers — hier beginnt das Aufziehen. */
    container: HTMLElement | null;
    /** Werkzeug „Entfernen“ gewählt? Markierungen sind immer sichtbar, markiert wird nur aktiv. */
    aktiv: boolean;
    stand: BearbeitungStand;
    dispatch: React.Dispatch<BearbeitungAktion>;
    /** Hinweis für die Leiste (z. B. verweigerte Zwischenablage); `null` löscht ihn. */
    onHinweis: (text: string | null) => void;
    /** Werkzeug „Schwärzen“ (T-363): neue Markierungen werden schwarze Balken, kein Strg+X. */
    schwaerzen?: boolean;
}> = ({ viewer, container, aktiv, stand, dispatch, onHinweis, schwaerzen = false }) => {
    const [, neuZeichnen] = React.useReducer((n: number) => n + 1, 0);
    const [zug, setZug] = React.useState<Zug | null>(null);
    const [ausgewaehlt, setAusgewaehlt] = React.useState<string | null>(null);
    const [knopfOrt, setKnopfOrt] = React.useState<{ left: number; top: number } | null>(null);
    const hosts = React.useRef(new Map<number, HTMLDivElement>());

    // Nach Zoom/Drehung/Neuzeichnen die Boxen neu berechnen.
    React.useEffect(() => {
        if (!viewer) return;
        const bus = viewer.eventBus;
        const fn = () => neuZeichnen();
        for (const e of RENDER_EREIGNISSE) bus.on(e, fn);
        return () => {
            for (const e of RENDER_EREIGNISSE) bus.off(e, fn);
        };
    }, [viewer]);

    // Aufräumen beim Verlassen des Editors.
    React.useEffect(() => {
        const map = hosts.current;
        return () => {
            for (const h of map.values()) h.remove();
            map.clear();
        };
    }, []);

    React.useEffect(() => {
        if (!container) return;
        const vorher = container.style.cursor;
        if (aktiv) container.style.cursor = "crosshair";
        return () => {
            container.style.cursor = vorher;
        };
    }, [aktiv, container]);

    const seitenAnsicht = React.useCallback(
        (i: number): SeitenAnsichtLike | null => (viewer ? viewer.getPageView(i) ?? null : null),
        [viewer],
    );

    /** Portal-Host der Seite (angelegt im Render, eingehängt im Layout-Effekt unten). */
    const hostFuer = (i: number): HTMLDivElement => {
        let host = hosts.current.get(i);
        if (!host) {
            host = document.createElement("div");
            host.className = "kmvs-entfernen-ebene";
            Object.assign(host.style, { position: "absolute", inset: "0", pointerEvents: "none", zIndex: "20" });
            hosts.current.set(i, host);
        }
        return host;
    };
    const benutzteSeiten = React.useRef(new Set<number>());

    // Nach jedem Render: Hosts benutzter Seiten (wieder) einhängen — PDF.js
    // leert die Seiten-Divs beim Zoomen —, die übrigen aushängen.
    React.useLayoutEffect(() => {
        for (const [i, host] of hosts.current) {
            const div = benutzteSeiten.current.has(i) ? seitenAnsicht(i)?.div : null;
            if (div && host.parentNode !== div) div.appendChild(host);
            else if (!div && host.parentNode) host.remove();
        }
    });

    // ─── Markieren ──────────────────────────────────────────────────────────

    const markiereAuswahl = React.useCallback(
        (sel: Selection): Markierung[] => {
            if (!viewer) return [];
            const text = sel.toString();
            // Nur Zeilenkästen aus den Textebenen — `range.getClientRects()` enthielte
            // bei seitenübergreifender Auswahl auch ganze Seiten (siehe auswahlText.ts).
            const clientRects = textRechteckeDerSelection(sel);
            const gruppe = neueId();
            const neu: Markierung[] = [];
            for (let i = 0; i < viewer.pagesCount; i++) {
                const ansicht = seitenAnsicht(i);
                if (!ansicht?.div) continue;
                const box = inhaltsBox(ansicht.div);
                // Jedes Zeilenstück gehört der Seite, in der seine Mitte liegt.
                const hier = clientRects.filter((r) => {
                    const mx = r.left + r.width / 2;
                    const my = r.top + r.height / 2;
                    return mx >= box.left && mx <= box.right && my >= box.top && my <= box.bottom;
                });
                for (const rect of auswahlZuRechtecken(hier, box, ansicht.viewport)) {
                    neu.push({ id: neueId(), seite: i, rect, quelle: "text", text, gruppe, ...(schwaerzen ? { schwarz: true } : {}) });
                }
            }
            if (neu.length) dispatch({ typ: "entfernenHinzu", markierungen: neu });
            sel.removeAllRanges();
            setKnopfOrt(null);
            return neu;
        },
        [dispatch, schwaerzen, seitenAnsicht, viewer],
    );

    // Rechteck aufziehen.
    React.useEffect(() => {
        if (!container || !aktiv || !viewer) return;
        // Laufender Zug — beim Abbruch (pointercancel), Werkzeugwechsel oder
        // Verlassen des Editors werden Listener und Rahmen abgeräumt.
        let zugBeenden: (() => void) | null = null;
        const onDown = (e: PointerEvent) => {
            if (e.button !== 0) return;
            zugBeenden?.();
            const ziel = e.target as HTMLElement | null;
            if (!ziel || typeof ziel.closest !== "function") return;
            // Klick auf eine Markierung/×, einen Ausschnitt oder ein Kommentar-Symbol/eine Blase.
            if (ziel.closest(".kmvs-entfernen-ebene, .kmvs-ausschnitt-ebene, .kmvs-kommentar-ebene")) return;
            const seitenDiv = ziel.closest<HTMLElement>(".page[data-page-number]");
            if (!seitenDiv) return;
            // Über Text wählt man Text aus — außer mit Alt.
            const aufText = !!ziel.closest(".textLayer span") && !ziel.classList.contains("endOfContent");
            if (aufText && !e.altKey) return;
            const seite = Number(seitenDiv.dataset.pageNumber) - 1;
            if (!seitenAnsicht(seite)) return;
            e.preventDefault(); // keine Textauswahl während des Ziehens
            const box = inhaltsBox(seitenDiv);
            const start = { x: e.clientX - box.left, y: e.clientY - box.top };
            let aktuell: Zug = { seite, x0: start.x, y0: start.y, x1: start.x, y1: start.y };
            setZug(aktuell);
            setAusgewaehlt(null);

            const klemme = (v: number, max: number) => Math.min(Math.max(v, 0), max);
            const onMove = (ev: PointerEvent) => {
                aktuell = {
                    ...aktuell,
                    x1: klemme(ev.clientX - box.left, box.width),
                    y1: klemme(ev.clientY - box.top, box.height),
                };
                setZug(aktuell);
            };
            const beenden = () => {
                alleFenster.removeEventListener("pointermove", onMove);
                alleFenster.removeEventListener("pointerup", onUp);
                alleFenster.removeEventListener("pointercancel", beenden);
                zugBeenden = null;
                setZug(null);
            };
            const onUp = (ev: PointerEvent) => {
                onMove(ev);
                beenden();
                if (Math.abs(aktuell.x1 - aktuell.x0) < MIN_ZUG || Math.abs(aktuell.y1 - aktuell.y0) < MIN_ZUG) return;
                const ansicht = seitenAnsicht(seite);
                if (!ansicht) return;
                const m: Markierung = {
                    id: neueId(),
                    seite,
                    rect: cssZuPdfRect(ansicht.viewport, aktuell.x0, aktuell.y0, aktuell.x1, aktuell.y1),
                    quelle: "rechteck",
                    ...(schwaerzen ? { schwarz: true } : {}),
                };
                dispatch({ typ: "entfernenHinzu", markierungen: [m] });
                setAusgewaehlt(m.id);
            };
            alleFenster.addEventListener("pointermove", onMove);
            alleFenster.addEventListener("pointerup", onUp);
            // Abbruch durch Browser/System (Touch-Geste, Fensterwechsel): nichts markieren.
            alleFenster.addEventListener("pointercancel", beenden);
            zugBeenden = beenden;
        };
        container.addEventListener("pointerdown", onDown);
        return () => {
            container.removeEventListener("pointerdown", onDown);
            zugBeenden?.();
        };
    }, [aktiv, container, dispatch, schwaerzen, seitenAnsicht, viewer]);

    // Schwebender Knopf an der Textauswahl.
    React.useEffect(() => {
        if (!aktiv) {
            setKnopfOrt(null);
            return;
        }
        const aktualisiere = () => {
            const sel = auswahlImViewer(container);
            if (!sel) return setKnopfOrt(null);
            const rects = textRechteckeDerSelection(sel);
            const letztes = rects[rects.length - 1];
            if (!letztes) return setKnopfOrt(null);
            setKnopfOrt({ left: letztes.left + letztes.width, top: letztes.top + letztes.height + 4 });
        };
        alleDokumente.addEventListener("selectionchange", aktualisiere);
        container?.addEventListener("scroll", aktualisiere);
        return () => {
            alleDokumente.removeEventListener("selectionchange", aktualisiere);
            container?.removeEventListener("scroll", aktualisiere);
        };
    }, [aktiv, container]);

    /** Ausschnitt einer Rechteck-Markierung als PNG in die Zwischenablage. */
    const bildAusschneiden = React.useCallback(
        (m: Markierung) => {
            const ansicht = seitenAnsicht(m.seite);
            const canvas = ansicht?.canvas;
            const verweigert = () =>
                onHinweis(
                    "Ausschneiden: Edge hat den Zugriff auf die Zwischenablage nicht erlaubt — der Bereich wird trotzdem entfernt.",
                );
            if (!ansicht || !canvas || typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
                verweigert();
                return;
            }
            const box = pdfZuCssRect(ansicht.viewport, m.rect);
            // Canvas-Pixel je CSS-Pixel (PDF.js rendert mit devicePixelRatio).
            const faktor = canvas.width / ansicht.viewport.width;
            const aus = document.createElement("canvas");
            aus.width = Math.max(1, Math.round(box.width * faktor));
            aus.height = Math.max(1, Math.round(box.height * faktor));
            aus.getContext("2d")?.drawImage(
                canvas,
                box.left * faktor,
                box.top * faktor,
                aus.width,
                aus.height,
                0,
                0,
                aus.width,
                aus.height,
            );
            // Das Blob als Promise übergeben: so bleibt der Aufruf innerhalb der
            // Nutzergeste, die der Browser für die Zwischenablage verlangt.
            const blob = new Promise<Blob>((resolve, reject) =>
                aus.toBlob((b) => (b ? resolve(b) : reject(new Error("kein Bild"))), "image/png"),
            );
            navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]).catch(verweigert);
        },
        [onHinweis, seitenAnsicht],
    );

    // Tastatur: Entf/Rücktaste und Strg+X.
    React.useEffect(() => {
        if (!aktiv) return;
        const onKey = (e: KeyboardEvent) => {
            if (istEingabe(e.target)) return;
            // Kürzel nur für den Viewer: nicht in Dialogen (z. B. Speichern) und
            // nicht, wenn der Fokus woanders liegt (Knopf der Leiste, anderes Fenster).
            const ziel = e.target as HTMLElement | null;
            if (ziel && typeof ziel.closest === "function" && ziel.closest("[role=dialog]")) return;
            const fokus = aktivesDokument().activeElement;
            if (fokus && fokus !== document.body && !container?.contains(fokus)) return;
            const sel = auswahlImViewer(container);
            if (e.key === "Delete" || e.key === "Backspace") {
                if (!sel) return;
                e.preventDefault();
                markiereAuswahl(sel);
                return;
            }
            // Schwärzen kennt kein Ausschneiden — der Inhalt soll ja gerade nirgends hin.
            if (!schwaerzen && (e.ctrlKey || e.metaKey) && (e.key === "x" || e.key === "X")) {
                if (sel) {
                    e.preventDefault();
                    const text = sel.toString();
                    const verweigert = () =>
                        onHinweis(
                            "Ausschneiden: Edge hat den Zugriff auf die Zwischenablage nicht erlaubt — der Text wird trotzdem entfernt.",
                        );
                    // Synchron in der Tastatur-Geste aufrufen — sonst verweigert der Browser.
                    try {
                        navigator.clipboard.writeText(text).catch(verweigert);
                    } catch {
                        verweigert();
                    }
                    markiereAuswahl(sel);
                    return;
                }
                const m = stand.entfernen.find((x) => x.id === ausgewaehlt && x.quelle === "rechteck");
                if (m) {
                    e.preventDefault();
                    bildAusschneiden(m);
                }
            }
        };
        alleDokumente.addEventListener("keydown", onKey);
        return () => alleDokumente.removeEventListener("keydown", onKey);
    }, [aktiv, ausgewaehlt, bildAusschneiden, container, markiereAuswahl, onHinweis, schwaerzen, stand.entfernen]);

    const zuruecknehmen = (m: Markierung) => {
        const weg = m.gruppe ? stand.entfernen.filter((x) => x.gruppe === m.gruppe) : [m];
        for (const x of weg) dispatch({ typ: "entfernenWeg", id: x.id });
        if (ausgewaehlt && weg.some((x) => x.id === ausgewaehlt)) setAusgewaehlt(null);
    };

    // ─── Zeichnen ───────────────────────────────────────────────────────────

    if (!viewer) return null;

    const jeSeite = new Map<number, Markierung[]>();
    for (const m of stand.entfernen) {
        // Quellen von Ausschnitten zeichnet die Ausschnitt-Ebene (Rahmen „Ursprung“);
        // zurückgenommen werden sie mit ihrem Ausschnitt, nicht per ×.
        if (m.quelle === "ausschnitt") continue;
        jeSeite.set(m.seite, [...(jeSeite.get(m.seite) ?? []), m]);
    }
    if (zug && !jeSeite.has(zug.seite)) jeSeite.set(zug.seite, []);

    benutzteSeiten.current = new Set(jeSeite.keys());
    const titel = "Wird beim Speichern aus dieser Fassung entfernt — × nimmt die Markierung zurück";
    const titelSchwarz = "Wird beim Speichern echt entfernt und geschwärzt — × nimmt die Schwärzung zurück";

    return (
        <>
            {[...jeSeite].map(([seite, liste]) => {
                const ansicht = seitenAnsicht(seite);
                if (!ansicht?.div) return null;
                const host = hostFuer(seite);
                // Das × nur einmal je Gruppe (eine mehrzeilige Auswahl = eine Stelle).
                const gezeigteGruppen = new Set<string>();
                return createPortal(
                    <>
                        {liste.map((m) => {
                            const box = pdfZuCssRect(ansicht.viewport, m.rect);
                            const mitKreuz = !m.gruppe || !gezeigteGruppen.has(m.gruppe);
                            if (m.gruppe) gezeigteGruppen.add(m.gruppe);
                            return (
                                <div
                                    key={m.id}
                                    data-markierung={m.id}
                                    title={m.schwarz ? titelSchwarz : titel}
                                    data-schwarz={m.schwarz ? "" : undefined}
                                    onClick={() => setAusgewaehlt(m.id)}
                                    style={{
                                        ...stil.markierung,
                                        ...(m.schwarz ? stil.schwarz : null),
                                        ...(ausgewaehlt === m.id ? stil.markierungAusgewaehlt : null),
                                        left: box.left,
                                        top: box.top,
                                        width: box.width,
                                        height: box.height,
                                    }}
                                >
                                    {mitKreuz && (
                                        <button
                                            type="button"
                                            aria-label="Markierung zurücknehmen"
                                            title="Markierung zurücknehmen — der Inhalt bleibt"
                                            style={stil.kreuz}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                zuruecknehmen(m);
                                            }}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                        {zug?.seite === seite && (
                            <div
                                data-zugrahmen=""
                                style={{
                                    ...stil.zug,
                                    left: Math.min(zug.x0, zug.x1),
                                    top: Math.min(zug.y0, zug.y1),
                                    width: Math.abs(zug.x1 - zug.x0),
                                    height: Math.abs(zug.y1 - zug.y0),
                                }}
                            />
                        )}
                    </>,
                    host,
                    `seite-${seite}`,
                );
            })}
            {aktiv && knopfOrt && (
                <button
                    type="button"
                    style={{ ...stil.knopf, left: knopfOrt.left, top: knopfOrt.top }}
                    title={
                        schwaerzen
                            ? "Ausgewählten Text beim Speichern echt entfernen und schwärzen"
                            : "Ausgewählten Text beim Speichern aus dieser Fassung entfernen"
                    }
                    // Kein Fokuswechsel — sonst wäre die Auswahl weg, bevor der Klick ankommt.
                    onPointerDown={(e) => e.preventDefault()}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                        const sel = auswahlImViewer(container);
                        if (sel) markiereAuswahl(sel);
                    }}
                >
                    {schwaerzen ? "Schwärzen (Entf)" : "Entfernen (Entf)"}
                </button>
            )}
        </>
    );
};
